import React, { forwardRef } from 'react';
import { BRAND, ROOF_LABELS, getRal, type CalculatorState, type Estimate, type EstimateLine, formatRub } from './calculator-config';

interface ProposalTemplateProps {
  state: CalculatorState;
  estimate: Estimate;
  estimateLines: EstimateLine[];
  imageRef: React.Ref<HTMLImageElement>;
  fallbackRef: React.Ref<HTMLDivElement>;
  illustration: React.ReactNode;
}

/** One HTML layout for the live preview and the generated customer PDF. */
export default forwardRef<HTMLDivElement, ProposalTemplateProps>(function ProposalTemplate(
  { state, estimate, estimateLines, imageRef, fallbackRef, illustration }, ref,
) {
  const ral = getRal(state.ral);
  const today = new Date().toLocaleDateString('ru-RU');
  return (
    <div ref={ref} data-testid="commercial-proposal" style={{ width: 760, margin: '0 auto', padding: 40, background: '#ffffff', fontFamily: 'Arial, sans-serif', color: '#0f172a' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #214688', paddingBottom: 16 }}>
            <div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#214688' }}>{BRAND.name}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Коммерческое предложение</div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 12, color: '#64748b' }}>
              <div>Дата: {today}</div>
              <div>{BRAND.phone}</div>
              <div>{BRAND.email}</div>
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <img
              ref={imageRef}
              alt="3D-визуализация объекта"
              style={{
                display: 'none',
                width: '100%',
                maxHeight: 300,
                objectFit: 'contain',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                background: '#dbeafe',
              }}
            />
            <div ref={fallbackRef} style={{ maxWidth: 480, margin: '0 auto' }}>{illustration}</div>
          </div>

          <h2 style={{ fontSize: 16, fontWeight: 700, margin: '24px 0 12px' }}>Параметры объекта</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              {[
                ['Город строительства', `${state.cityName} (снеговой ${estimate.snow}, ветровой ${estimate.wind})`],
                ['Габариты (Ш×Д×В)', `${state.width} × ${state.length} × ${state.height} м`],
                ['Площадь основания', `${estimate.area} м²`],
                ['Кровля', ROOF_LABELS[state.roof]],
                ['Исполнение', state.warm ? 'Тёплое (сэндвич-панель)' : 'Холодное (профлист)'],
                ['Кран-балка (3,2 т)', state.crane ? 'Да (усиление каркаса)' : 'Нет'],
                ['Цвет', `RAL ${ral.ral} — ${ral.name}`],
                ['Окна / Ворота', `${state.windows} шт / ${state.gates} шт`],
              ].map(([k, v]) => (
                <tr key={k as string}>
                  <td style={{ padding: '8px 0', color: '#64748b', borderBottom: '1px solid #e2e8f0', width: '45%' }}>{k}</td>
                  <td style={{ padding: '8px 0', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2 style={{ fontSize: 16, fontWeight: 700, margin: '24px 0 12px' }}>Смета проекта</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Наименование', 'Кол-во', 'Ставка', 'Сумма'].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      padding: '6px 0',
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      color: '#94a3b8',
                      textAlign: i === 0 ? 'left' : 'right',
                      borderBottom: '2px solid #e2e8f0',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {estimateLines.map((line, i) => (
                <React.Fragment key={line.title}><tr>
                  <td style={{ padding: '10px 8px 10px 0', borderBottom: '1px solid #e2e8f0', width: '52%', verticalAlign: 'top' }}>
                    <div style={{ fontWeight: 700 }}>{i + 1}. {line.title}</div>
                    <div style={{ marginTop: 4, fontSize: 11, lineHeight: 1.5, color: '#64748b' }}>{line.includes}</div>
                    {line.coeffNote && (
                      <div style={{ marginTop: 4, fontSize: 11, lineHeight: 1.5, color: '#64748b' }}>
                        Коэффициент {line.coeffLabel} — {line.coeffNote}.
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 0', textAlign: 'right', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    {line.qty.toLocaleString('ru-RU')} {line.unit}
                  </td>
                  <td style={{ padding: '10px 0 10px 12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    {line.price.toLocaleString('ru-RU')} ₽/{line.unit}
                    {line.coeffLabel && <div style={{ fontSize: 11, color: '#64748b' }}>× {line.coeffLabel}</div>}
                  </td>
                  <td style={{ padding: '10px 0 10px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    {formatRub(line.sum)}
                  </td>
                </tr>
                <tr>
                  <td colSpan={4} style={{ padding: '8px 12px 12px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 11, lineHeight: 1.6, color: '#475569' }}>
                    {line.calculation.map((detail) => <div key={detail}>{detail}</div>)}
                  </td>
                </tr></React.Fragment>
              ))}
              <tr>
                <td style={{ padding: '12px 0', fontSize: 15, fontWeight: 800, color: '#214688' }}>Итого</td>
                <td colSpan={3} style={{ padding: '12px 0', textAlign: 'right', fontSize: 18, fontWeight: 800, color: '#214688' }}>
                  {formatRub(estimate.total)}
                </td>
              </tr>
            </tbody>
          </table>

          <p style={{ fontSize: 11, color: '#475569', marginTop: 24, lineHeight: 1.5 }}>
            Стеновые ограждения рассчитаны по площади стен. Покрытие кровли рассчитывается
            отдельно по проекту и не входит в этот итог. Толщину и комплектацию стеновых материалов уточняет
            инженерный отдел по проекту.
          </p>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 12, lineHeight: 1.5 }}>
            Расчёт предварительный и не является публичной офертой. Точную стоимость рассчитывает
            инженерный отдел {BRAND.name} после уточнения проекта. Сайт: {BRAND.site}.
          </p>
    </div>
  );
});
